import {
  areDatesInTheSameWeek,
  getWeekDatesRange,
  getOffsetWeekDatesRange,
  getCaptionText,
} from './dateUtils';
import { endOfDay, startOfDay } from 'date-fns';

describe('Date utils', () => {
  describe('getWeekDatesRange', () => {
    const date = new Date('2021-06-29');

    it('should compute week range for the given date when first day of the week is Sunday', () => {
      const firstDayOfTheWeek = 0;
      const expectedDateRange = {
        startOfWeek: startOfDay(new Date('2021-06-27')),
        endOfWeek: endOfDay(new Date('2021-07-03')),
      };

      const datesRange = getWeekDatesRange(date, firstDayOfTheWeek);

      expect(datesRange).toEqual(expectedDateRange);
    });

    it('should compute week range for the given date when first day of the week is Monday', () => {
      const firstDayOfTheWeek = 1;
      const expectedDateRange = {
        startOfWeek: startOfDay(new Date('2021-06-28')),
        endOfWeek: endOfDay(new Date('2021-07-04')),
      };

      const datesRange = getWeekDatesRange(date, firstDayOfTheWeek);

      expect(datesRange).toEqual(expectedDateRange);
    });

    it('should compute week range for the given date when first day of the week is Tuesday', () => {
      const firstDayOfTheWeek = 2;
      const expectedDateRange = {
        startOfWeek: startOfDay(new Date('2021-06-29')),
        endOfWeek: endOfDay(new Date('2021-07-05')),
      };

      const datesRange = getWeekDatesRange(date, firstDayOfTheWeek);

      expect(datesRange).toEqual(expectedDateRange);
    });

    it('should compute week range for the given date when first day of the week is Wednesday', () => {
      const firstDayOfTheWeek = 3;
      const expectedDateRange = {
        startOfWeek: startOfDay(new Date('2021-06-23')),
        endOfWeek: endOfDay(new Date('2021-06-29')),
      };

      const datesRange = getWeekDatesRange(date, firstDayOfTheWeek);

      expect(datesRange).toEqual(expectedDateRange);
    });

    it('should compute week range for the given date when first day of the week is Thursday', () => {
      const firstDayOfTheWeek = 4;
      const expectedDateRange = {
        startOfWeek: startOfDay(new Date('2021-06-24')),
        endOfWeek: endOfDay(new Date('2021-06-30')),
      };

      const datesRange = getWeekDatesRange(date, firstDayOfTheWeek);

      expect(datesRange).toEqual(expectedDateRange);
    });

    it('should compute week range for the given date when first day of the week is Friday', () => {
      const firstDayOfTheWeek = 5;
      const expectedDateRange = {
        startOfWeek: startOfDay(new Date('2021-06-25')),
        endOfWeek: endOfDay(new Date('2021-07-01')),
      };

      const datesRange = getWeekDatesRange(date, firstDayOfTheWeek);

      expect(datesRange).toEqual(expectedDateRange);
    });

    it('should compute week range for the given date when first day of the week is Saturday', () => {
      const firstDayOfTheWeek = 6;
      const expectedDateRange = {
        startOfWeek: startOfDay(new Date('2021-06-26')),
        endOfWeek: endOfDay(new Date('2021-07-02')),
      };

      const datesRange = getWeekDatesRange(date, firstDayOfTheWeek);

      expect(datesRange).toEqual(expectedDateRange);
    });

    it('should have Monday as the default first of the week', () => {
      const expectedDateRange = {
        startOfWeek: startOfDay(new Date('2021-06-28')),
        endOfWeek: endOfDay(new Date('2021-07-04')),
      };

      const datesRange = getWeekDatesRange(date);

      expect(datesRange).toEqual(expectedDateRange);
    });
  });

  describe('areDatesInTheSameWeek', () => {
    it('should return true if the two dates are in the same week', () => {
      const firstDayOfTheWeek = 0;
      const firstDate = new Date('2021-06-27');
      const secondDate = new Date('2021-07-03');

      expect(
        areDatesInTheSameWeek(firstDate, secondDate, firstDayOfTheWeek)
      ).toBe(true);
    });

    it('should return false if the two dates are not in the same week', () => {
      const firstDayOfTheWeek = 1;
      const firstDate = new Date('2021-06-27');
      const secondDate = new Date('2021-07-03');

      expect(
        areDatesInTheSameWeek(firstDate, secondDate, firstDayOfTheWeek)
      ).toBe(false);
    });

    it('should have Monday as the default first of the week', () => {
      const firstDate = new Date('2021-06-27');
      const secondDate = new Date('2021-07-03');

      expect(areDatesInTheSameWeek(firstDate, secondDate)).toBe(false);
    });
  });

  describe('getOffsetWeekDatesRange', () => {
    it('should return correct week range when offset is 0', () => {
      const date = new Date(2022, 5, 14); // June 14, 2022
      const result = getOffsetWeekDatesRange(date, 0, {
        locale: 'en',
        firstDayOfWeek: 1, // Monday
      });

      const expected = getWeekDatesRange(date, 1);
      expect(result.startOfWeek).toEqual(expected.startOfWeek);
      expect(result.endOfWeek).toEqual(expected.endOfWeek);
    });

    it('should return correct week range when offset is positive', () => {
      const date = new Date(2022, 5, 14); // June 14, 2022
      const result = getOffsetWeekDatesRange(date, 1, {
        locale: 'en',
        firstDayOfWeek: 1, // Monday
      });

      const expected = getWeekDatesRange(new Date(2022, 5, 21), 1);
      expect(result.startOfWeek).toEqual(expected.startOfWeek);
      expect(result.endOfWeek).toEqual(expected.endOfWeek);
    });

    it('should return correct week range when offset is negative', () => {
      const date = new Date(2022, 5, 14); // June 14, 2022
      const result = getOffsetWeekDatesRange(date, -1, {
        locale: 'en',
        firstDayOfWeek: 1, // Monday
      });

      const expected = getWeekDatesRange(new Date(2022, 5, 7), 1);
      expect(result.startOfWeek).toEqual(expected.startOfWeek);
      expect(result.endOfWeek).toEqual(expected.endOfWeek);
    });

    it('should handle different firstDayOfWeek values', () => {
      const date = new Date(2022, 5, 14); // June 14, 2022
      const result = getOffsetWeekDatesRange(date, 0, {
        locale: 'en',
        firstDayOfWeek: 0, // Sunday
      });

      const expected = getWeekDatesRange(date, 0);
      expect(result.startOfWeek).toEqual(expected.startOfWeek);
      expect(result.endOfWeek).toEqual(expected.endOfWeek);
    });
  });

  describe('getCaptionText', () => {
    it('should return correct caption for single month', () => {
      const weekRange = {
        startOfWeek: new Date(2022, 5, 13), // June 13, 2022
        endOfWeek: new Date(2022, 5, 19), // June 19, 2022
      };

      const result = getCaptionText(weekRange, 'en');
      expect(result).toBe('June 2022');
    });

    it('should return correct caption for week spanning two months', () => {
      const weekRange = {
        startOfWeek: new Date(2022, 5, 28), // June 28, 2022
        endOfWeek: new Date(2022, 6, 4), // July 4, 2022
      };

      const result = getCaptionText(weekRange, 'en');
      expect(result).toBe('Jun - Jul 2022');
    });

    it('should handle different locales', () => {
      const weekRange = {
        startOfWeek: new Date(2022, 5, 13), // June 13, 2022
        endOfWeek: new Date(2022, 5, 19), // June 19, 2022
      };

      const result = getCaptionText(weekRange, 'he');
      expect(result).toBe('יוני 2022');
    });

    it('should handle different firstDayOfWeek values', () => {
      const weekRange = {
        startOfWeek: new Date(2022, 5, 12), // June 12, 2022 (Sunday)
        endOfWeek: new Date(2022, 5, 18), // June 18, 2022 (Saturday)
      };

      const result = getCaptionText(weekRange, 'en'); // Sunday as first day
      expect(result).toBe('June 2022');
    });
  });
});
